import AddFilterButton from '@/components/Filter/AddFilterButton';
import FilterChip from '@/components/Filter/FilterChip';
import { CampaignsTabButton, ChannelTypesTabButton, PacingStatusTabButton } from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { FILTERS_BASE_TAB_ID, FILTERS_CHANNELS_OPTIONS, STATUS_LABELS } from '@/constants/campaignManagement';
import { PACING_STATUS_OPTIONS } from '@/constants/pacing';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { getCampaigns } from '@/store/slices/campaignManagementSlice';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const CAMPAIGNS_TAB_INDEX = 0;
const PACING_STATUS_TAB_INDEX = 1;
const CHANNEL_TYPES_TAB_INDEX = 2;

const STATUS_FILTER_KEY = 'status';

const tabData = [
  {
    index: CAMPAIGNS_TAB_INDEX,
    label: 'Campaign',
    component: CampaignsTabButton,
    optionsKey: 'campaigns',
  },
  {
    index: PACING_STATUS_TAB_INDEX,
    label: 'Pacing Status',
    component: PacingStatusTabButton,
    optionsKey: 'pacingStatus',
    options: PACING_STATUS_OPTIONS,
  },
  {
    index: CHANNEL_TYPES_TAB_INDEX,
    label: 'Channel Type',
    component: ChannelTypesTabButton,
    optionsKey: 'channelTypes',
    options: FILTERS_CHANNELS_OPTIONS,
  },
];

const FilterPopover = ({ filters, setFilters }) => {
  const dispatch = useDispatch();

  const [currentTab, setCurrentTab] = useState(CAMPAIGNS_TAB_INDEX);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [pendingFilters, setPendingFilters] = useState({
    campaigns: filters.campaigns,
    pacingStatus: filters.pacingStatus,
    channelTypes: filters.channelTypes,
  });

  const { campaigns, areCampaignsLoading } = useSelector(({ campaignManagement }) => ({
    campaigns: campaignManagement?.campaigns ?? [],
    areCampaignsLoading: !!campaignManagement?.campaignsRequestId,
  }));

  useEffect(() => {
    dispatch(
      getCampaigns({
        clients: filters.clients,
        advertisers: filters.advertisers,
      }),
    );
  }, [dispatch, filters.advertisers, filters.clients]);

  const resetPendingValues = useCallback(() => {
    setPendingFilters({
      campaigns: filters.campaigns,
      pacingStatus: filters.pacingStatus,
      channelTypes: filters.channelTypes,
    });
  }, [filters]);

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
  };

  const handleOpen = ({ currentTarget }) => {
    resetPendingValues();
    setFilterAnchorEl(currentTarget);
  };

  const handleClose = (_event, reason) => {
    if (reason) return;
    setFilterAnchorEl(null);
    resetPendingValues();
  };

  const handleSubmit = () => {
    if (
      areArraysOfObjectsEqual(filters.campaigns, pendingFilters.campaigns) &&
      areArraysOfObjectsEqual(filters.pacingStatus, pendingFilters.pacingStatus) &&
      areArraysOfObjectsEqual(filters.channelTypes, pendingFilters.channelTypes)
    ) {
      handleClose();
      return;
    }
    setFilters(pendingFilters);
    handleClose();
  };

  const updatePendingValue =
    key =>
    (updatedValue = []) => {
      setPendingFilters(prev => ({ ...prev, [key]: updatedValue }));
    };

  const removeFilter = (key, item) => () => {
    const updatedValue = filters[key].filter(filterItem =>
      key === STATUS_FILTER_KEY ? filterItem !== item : filterItem.value !== item.value,
    );
    setFilters({ ...filters, [key]: updatedValue });
  };

  const filtersOpen = Boolean(filterAnchorEl);
  const filtersId = filtersOpen ? 'filters-popover' : undefined;

  const appliedFilters = useMemo(
    () => Object.entries(filters).filter(([_, value]) => Array.isArray(value) && value.length),
    [
      filters.clients,
      filters.advertisers,
      filters.campaigns,
      filters.status,
      filters.pacingStatus,
      filters.channelTypes,
    ],
  );

  return (
    <>
      <AddFilterButton onClick={handleOpen} describedBy={filtersId} />
      <TabFilterPopover
        id={filtersId}
        open={filtersOpen}
        anchorEl={filterAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          orientation='vertical'
          aria-label='pacing filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          {tabData.map(({ index, component }) => (
            <Tab key={index} component={component} {...a11yProps(index, FILTERS_BASE_TAB_ID)} />
          ))}
        </Tabs>
        {tabData.map(({ index, label, optionsKey, options }) => (
          <TabPanel key={index} className='w-full' baseTabId={FILTERS_BASE_TAB_ID} value={currentTab} index={index}>
            <TabSelect
              label={label}
              loading={index === CAMPAIGNS_TAB_INDEX && areCampaignsLoading}
              options={index === CAMPAIGNS_TAB_INDEX ? campaigns : options}
              value={pendingFilters[optionsKey]}
              onChange={updatePendingValue(optionsKey)}
              onClose={handleClose}
              onSubmit={handleSubmit}
            />
          </TabPanel>
        ))}
      </TabFilterPopover>
      {appliedFilters.map(([key, value]) =>
        value.map((item, idx) => (
          <FilterChip
            key={`${key}_${idx}`}
            type={key}
            label={key === STATUS_FILTER_KEY ? STATUS_LABELS[item] : item.label}
            onRemove={removeFilter(key, item)}
          />
        )),
      )}
    </>
  );
};

FilterPopover.propTypes = {
  filters: PropTypes.object,
  setFilters: PropTypes.func,
};

export default FilterPopover;
